// ============================================
// 1. GovernmentIntel.js - Full Government Data Hub
// ============================================

import React, { useState, useEffect } from 'react';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const GovernmentIntel = () => {
  const [selectedTab, setSelectedTab] = useState('congressional');
  const [autoRefresh, setAutoRefresh] = useState(true);

  // Government Intelligence Data
  const [congressionalTrades, setCongressionalTrades] = useState([
    {
      id: 1,
      date: '2025-08-02',
      politician: 'Nancy Pelosi',
      party: 'D',
      ticker: 'NVDA',
      action: 'Purchase',
      amount: '$1M-5M',
      currentPrice: 448.25,
      daysSince: 1,
      priceChange: 2.3,
      smartScore: 9.2,
      priority: 'High',
      catalyst: 'AI Infrastructure Bill Discussion'
    },
    {
      id: 2,
      date: '2025-08-01',
      politician: 'Josh Gottheimer',
      party: 'D',
      ticker: 'LMT',
      action: 'Purchase',
      amount: '$250K-500K',
      currentPrice: 422.10,
      daysSince: 2,
      priceChange: 1.8,
      smartScore: 8.9,
      priority: 'High',
      catalyst: 'Defense Spending Authorization'
    },
    {
      id: 3,
      date: '2025-07-31',
      politician: 'Dan Crenshaw',
      party: 'R',
      ticker: 'RTX',
      action: 'Purchase',
      amount: '$100K-250K',
      currentPrice: 118.45,
      daysSince: 3,
      priceChange: 0.9,
      smartScore: 8.1,
      priority: 'Medium',
      catalyst: 'Military Contract Discussion'
    },
    {
      id: 4,
      date: '2025-07-30',
      politician: 'Alexandria Ocasio-Cortez',
      party: 'D',
      ticker: 'TSLA',
      action: 'Sale',
      amount: '$500K-1M',
      currentPrice: 235.85,
      daysSince: 4,
      priceChange: -3.2,
      smartScore: 7.8,
      priority: 'Medium',
      catalyst: 'EV Subsidy Policy Changes'
    }
  ]);

  const [upcomingEvents, setUpcomingEvents] = useState([
    {
      date: '2025-08-05',
      event: 'Senate Defense Appropriations Committee',
      impact: 'High',
      sectors: ['Defense', 'Aerospace'],
      tickers: ['LMT', 'RTX', 'BA', 'NOC'],
      description: 'Vote on $900B defense spending bill'
    },
    {
      date: '2025-08-07',
      event: 'House AI Oversight Hearing',
      impact: 'High',
      sectors: ['Technology', 'AI'],
      tickers: ['NVDA', 'GOOGL', 'MSFT', 'META'],
      description: 'AI regulation and safety standards discussion'
    },
    {
      date: '2025-08-09',
      event: 'FDA Advisory Committee',
      impact: 'Medium',
      sectors: ['Biotech', 'Pharma'],
      tickers: ['MRNA', 'PFE', 'JNJ'],
      description: 'COVID variant vaccine approval'
    }
  ]);

  const [contractAwards, setContractAwards] = useState([
    {
      date: '2025-08-02',
      company: 'Lockheed Martin',
      ticker: 'LMT',
      amount: '$2.3B',
      type: 'Defense',
      description: 'F-35 Fighter Jet Maintenance Contract',
      duration: '5 years',
      priceImpact: 2.1,
      smartScore: 9.1
    },
    {
      date: '2025-08-01',
      company: 'Palantir Technologies',
      ticker: 'PLTR',
      amount: '$178M',
      type: 'Intelligence',
      description: 'Army Data Analytics Platform',
      duration: '3 years',
      priceImpact: 8.4,
      smartScore: 8.7
    },
    {
      date: '2025-07-31',
      company: 'Microsoft Corporation',
      ticker: 'MSFT',
      amount: '$412M',
      type: 'Technology',
      description: 'Cloud Infrastructure Upgrade',
      duration: '2 years',
      priceImpact: 0.8,
      smartScore: 7.9
    }
  ]);

  // Helper Functions
  const formatCurrency = (value) => new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2
  }).format(value);

  const formatPercent = (value) => `${value > 0 ? '+' : ''}${value.toFixed(1)}%`;

  const getPriorityColor = (priority) => {
    switch(priority) {
      case 'High': return '#FF0000';
      case 'Medium': return '#FFB800';
      case 'Low': return '#00FF5F';
      default: return '#a0a9c1';
    }
  };

  const getPartyColor = (party) => {
    return party === 'D' ? '#0069B1' : '#FF0000';
  };

  const getSmartScoreColor = (score) => {
    if (score >= 8.5) return '#00FF5F';
    if (score >= 7.0) return '#FFB800';
    return '#FF0000';
  };

  const renderCongressionalTab = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      {/* Summary Stats */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '20px'
      }}>
        <div style={{
          backgroundColor: '#202A44',
          padding: '20px',
          borderRadius: '8px',
          border: '1px solid #3a4a6b',
          textAlign: 'center'
        }}>
          <div style={{ color: '#a0a9c1', fontSize: '14px', marginBottom: '8px', fontFamily: 'Georgia, serif' }}>
            Today's Trades
          </div>
          <div style={{ color: '#ffffff', fontSize: '24px', fontWeight: 'bold', fontFamily: 'Georgia, serif' }}>
            {congressionalTrades.filter(t => t.daysSince <= 1).length}
          </div>
        </div>
        <div style={{
          backgroundColor: '#202A44',
          padding: '20px',
          borderRadius: '8px',
          border: '1px solid #3a4a6b',
          textAlign: 'center'
        }}>
          <div style={{ color: '#a0a9c1', fontSize: '14px', marginBottom: '8px', fontFamily: 'Georgia, serif' }}>
            High Priority
          </div>
          <div style={{ color: '#FF0000', fontSize: '24px', fontWeight: 'bold', fontFamily: 'Georgia, serif' }}>
            {congressionalTrades.filter(t => t.priority === 'High').length}
          </div>
        </div>
        <div style={{
          backgroundColor: '#202A44',
          padding: '20px',
          borderRadius: '8px',
          border: '1px solid #3a4a6b',
          textAlign: 'center'
        }}>
          <div style={{ color: '#a0a9c1', fontSize: '14px', marginBottom: '8px', fontFamily: 'Georgia, serif' }}>
            Avg Performance
          </div>
          <div style={{ color: '#00FF5F', fontSize: '24px', fontWeight: 'bold', fontFamily: 'Georgia, serif' }}>
            +1.7%
          </div>
        </div>
        <div style={{
          backgroundColor: '#202A44',
          padding: '20px',
          borderRadius: '8px',
          border: '1px solid #3a4a6b',
          textAlign: 'center'
        }}>
          <div style={{ color: '#a0a9c1', fontSize: '14px', marginBottom: '8px', fontFamily: 'Georgia, serif' }}>
            Success Rate
          </div>
          <div style={{ color: '#00FF5F', fontSize: '24px', fontWeight: 'bold', fontFamily: 'Georgia, serif' }}>
            73%
          </div>
        </div>
      </div>

      {/* Congressional Trades Table */}
      <div style={{
        backgroundColor: '#202A44',
        padding: '24px',
        borderRadius: '8px',
        border: '1px solid #3a4a6b'
      }}>
        <h3 style={{ color: '#D4AF37', margin: '0 0 16px 0', fontFamily: 'Georgia, serif' }}>
          🏛️ Recent Congressional Trades
        </h3>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #3a4a6b' }}>
                <th style={{ padding: '12px', textAlign: 'left', color: '#a0a9c1', fontFamily: 'Georgia, serif' }}>Date</th>
                <th style={{ padding: '12px', textAlign: 'left', color: '#a0a9c1', fontFamily: 'Georgia, serif' }}>Politician</th>
                <th style={{ padding: '12px', textAlign: 'left', color: '#a0a9c1', fontFamily: 'Georgia, serif' }}>Ticker</th>
                <th style={{ padding: '12px', textAlign: 'left', color: '#a0a9c1', fontFamily: 'Georgia, serif' }}>Action</th>
                <th style={{ padding: '12px', textAlign: 'left', color: '#a0a9c1', fontFamily: 'Georgia, serif' }}>Amount</th>
                <th style={{ padding: '12px', textAlign: 'left', color: '#a0a9c1', fontFamily: 'Georgia, serif' }}>Performance</th>
                <th style={{ padding: '12px', textAlign: 'center', color: '#a0a9c1', fontFamily: 'Georgia, serif' }}>Priority</th>
                <th style={{ padding: '12px', textAlign: 'center', color: '#a0a9c1', fontFamily: 'Georgia, serif' }}>Smart Score</th>
              </tr>
            </thead>
            <tbody>
              {congressionalTrades.map((trade) => (
                <tr key={trade.id} style={{ borderBottom: '1px solid #3a4a6b' }}>
                  <td style={{ padding: '12px', color: '#ffffff', fontFamily: 'Georgia, serif' }}>
                    {new Date(trade.date).toLocaleDateString()}
                  </td>
                  <td style={{ padding: '12px', fontFamily: 'Georgia, serif' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{
                        backgroundColor: getPartyColor(trade.party),
                        color: '#ffffff',
                        padding: '2px 6px',
                        borderRadius: '4px',
                        fontSize: '10px',
                        fontWeight: 'bold'
                      }}>
                        {trade.party}
                      </span>
                      <span style={{ color: '#ffffff' }}>{trade.politician}</span>
                    </div>
                  </td>
                  <td style={{ padding: '12px', color: '#ffffff', fontFamily: 'Georgia, serif', fontWeight: 'bold' }}>
                    {trade.ticker}
                  </td>
                  <td style={{ padding: '12px', fontFamily: 'Georgia, serif' }}>
                    <span style={{
                      backgroundColor: trade.action === 'Purchase' ? '#00FF5F' : '#FF0000',
                      color: '#ffffff',
                      padding: '4px 8px',
                      borderRadius: '4px',
                      fontSize: '12px',
                      fontWeight: 'bold'
                    }}>
                      {trade.action}
                    </span>
                  </td>
                  <td style={{ padding: '12px', color: '#ffffff', fontFamily: 'Georgia, serif' }}>
                    {trade.amount}
                  </td>
                  <td style={{ padding: '12px', fontFamily: 'Georgia, serif' }}>
                    <div>
                      <div style={{ color: '#ffffff' }}>{formatCurrency(trade.currentPrice)}</div>
                      <div style={{
                        color: trade.priceChange > 0 ? '#00FF5F' : '#FF0000',
                        fontSize: '12px'
                      }}>
                        {formatPercent(trade.priceChange)} ({trade.daysSince}d)
                      </div>
                    </div>
                  </td>
                  <td style={{ padding: '12px', textAlign: 'center' }}>
                    <span style={{
                      color: getPriorityColor(trade.priority),
                      fontWeight: 'bold',
                      fontFamily: 'Georgia, serif'
                    }}>
                      {trade.priority}
                    </span>
                  </td>
                  <td style={{ padding: '12px', textAlign: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                      <div style={{
                        width: '12px',
                        height: '12px',
                        borderRadius: '50%',
                        backgroundColor: getSmartScoreColor(trade.smartScore)
                      }}></div>
                      <span style={{ color: '#ffffff', fontFamily: 'Georgia, serif' }}>
                        {trade.smartScore}/10
                      </span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  const renderEventsTab = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      {upcomingEvents.map((event, index) => (
        <div key={index} style={{
          backgroundColor: '#202A44',
          padding: '24px',
          borderRadius: '8px',
          border: '1px solid #3a4a6b'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '16px'
          }}>
            <div>
              <h3 style={{ color: '#D4AF37', margin: '0', fontFamily: 'Georgia, serif' }}>
                {event.event}
              </h3>
              <div style={{ color: '#a0a9c1', fontSize: '14px', fontFamily: 'Georgia, serif' }}>
                {new Date(event.date).toLocaleDateString()} • {event.sectors.join(', ')}
              </div>
            </div>
            <div style={{
              backgroundColor: event.impact === 'High' ? '#FF0000' : '#FFB800',
              color: '#ffffff',
              padding: '6px 12px',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: 'bold',
              fontFamily: 'Georgia, serif'
            }}>
              {event.impact} Impact
            </div>
          </div>
          <div style={{ color: '#ffffff', fontSize: '16px', marginBottom: '16px', fontFamily: 'Georgia, serif' }}>
            {event.description}
          </div>
          <div>
            <div style={{ color: '#a0a9c1', fontSize: '14px', marginBottom: '8px', fontFamily: 'Georgia, serif' }}>
              Watch List:
            </div>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {event.tickers.map((ticker, idx) => (
                <span key={idx} style={{
                  backgroundColor: '#5BC0DE',
                  color: '#ffffff',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  fontSize: '12px',
                  fontWeight: 'bold',
                  fontFamily: 'Georgia, serif'
                }}>
                  {ticker}
                </span>
              ))}
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  const renderContractsTab = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      {contractAwards.map((contract, index) => (
        <div key={index} style={{
          backgroundColor: '#202A44',
          padding: '24px',
          borderRadius: '8px',
          border: '1px solid #3a4a6b'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '16px'
          }}>
            <div>
              <h3 style={{ color: '#D4AF37', margin: '0', fontFamily: 'Georgia, serif' }}>
                {contract.company} ({contract.ticker})
              </h3>
              <div style={{ color: '#a0a9c1', fontSize: '14px', fontFamily: 'Georgia, serif' }}>
                {new Date(contract.date).toLocaleDateString()} • {contract.type} • {contract.duration}
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ color: '#00FF5F', fontSize: '20px', fontWeight: 'bold', fontFamily: 'Georgia, serif' }}>
                {contract.amount}
              </div>
              <div style={{ color: '#FFB800', fontSize: '14px', fontFamily: 'Georgia, serif' }}>
                +{contract.priceImpact}% today
              </div>
            </div>
          </div>
          <div style={{ color: '#ffffff', fontSize: '16px', marginBottom: '16px', fontFamily: 'Georgia, serif' }}>
            {contract.description}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <div>
              <span style={{ color: '#a0a9c1', fontSize: '14px', fontFamily: 'Georgia, serif' }}>Smart Score: </span>
              <span style={{
                color: getSmartScoreColor(contract.smartScore),
                fontWeight: 'bold',
                fontFamily: 'Georgia, serif'
              }}>
                {contract.smartScore}/10
              </span>
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div style={{
      position: 'relative',
      minHeight: '100vh',
      backgroundColor: '#1a2332',
      color: '#ffffff',
      fontFamily: 'Georgia, serif',
      padding: '24px'
    }}>
      {/* WATERMARK */}
      <div style={{
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        opacity: '0.03',
        pointerEvents: 'none',
        zIndex: '1',
        width: '800px',
        height: '800px'
      }}>
        <img 
          src="/finance-watermark.jpg"
          alt="Financial Trading Watermark"
          style={{ width: '100%', height: '100%', objectFit: 'contain' }}
        />
      </div>

      <div style={{ position: 'relative', zIndex: '2', maxWidth: '1400px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '32px'
        }}>
          <div>
            <h1 style={{
              color: '#ffffff',
              fontSize: '32px',
              fontWeight: 'bold',
              margin: '0',
              fontFamily: 'Georgia, serif'
            }}>🏛️ Government Intelligence</h1>
            <p style={{
              color: '#a0a9c1',
              fontSize: '16px',
              margin: '8px 0 0 0',
              fontFamily: 'Georgia, serif'
            }}>Real-time government catalyst monitoring and analysis</p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#a0a9c1', fontFamily: 'Georgia, serif' }}>
              <input 
                type="checkbox" 
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
              />
              Auto-refresh (30s)
            </label>
            <button style={{
              backgroundColor: '#0069B1',
              color: '#ffffff',
              border: 'none',
              borderRadius: '4px',
              padding: '8px 16px',
              cursor: 'pointer',
              fontFamily: 'Georgia, serif'
            }}>
              🔄 Refresh Now
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div style={{
          display: 'flex',
          gap: '8px',
          marginBottom: '24px'
        }}>
          {[
            { id: 'congressional', label: '🏛️ Congressional Trades' },
            { id: 'events', label: '📅 Upcoming Events' },
            { id: 'contracts', label: '📄 Contract Awards' }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setSelectedTab(tab.id)}
              style={{
                backgroundColor: selectedTab === tab.id ? '#D4AF37' : '#202A44',
                color: selectedTab === tab.id ? '#202A44' : '#ffffff',
                border: '1px solid #3a4a6b',
                borderRadius: '4px',
                padding: '12px 20px',
                cursor: 'pointer',
                fontFamily: 'Georgia, serif',
                fontWeight: '600'
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {selectedTab === 'congressional' && renderCongressionalTab()}
        {selectedTab === 'events' && renderEventsTab()}
        {selectedTab === 'contracts' && renderContractsTab()}
      </div>
    </div>
  );
};

export default GovernmentIntel;